'use client';

import * as React from 'react';
// Import specific components including Root
import { Root as TabsRoot, List as TabsListPrimitive, Trigger as TabsTriggerPrimitive, Content as TabsContentPrimitive } from '@radix-ui/react-tabs';
import { cn } from '@/lib/utils';
// Remove unused buttonVariants and ButtonProps
// import { buttonVariants, type ButtonProps } from '@/components/ui/button';

// // Context for Tabs (Potentially unused now)
// interface TabsContextType {
//   value: string;
//   onChange: (value: string) => void;
// }
// const TabsContext = React.createContext<TabsContextType | null>(null);

// // --- Remove or comment out the faulty Tabs component --- 
// export interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
//   defaultValue?: string;
//   value?: string;
//   onValueChange?: (value: string) => void;
// }

// const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
//   ({ className, defaultValue, value, onValueChange, ...props }, ref) => {
//     // ... (Implementation removed) ...
//   }
// );
// Tabs.displayName = 'Tabs';

// Keep TabsList, TabsTrigger, TabsContent as they seem correct according to shadcn/Radix

// Remove empty interface TabsListProps
// export interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {}

// Keep TabsList definition, but use TabsListPrimitive
const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsListPrimitive>,
  React.ComponentPropsWithoutRef<typeof TabsListPrimitive>
>(({ className, ...props }, ref) => (
    <TabsListPrimitive // Use imported primitive
      ref={ref}
      className={cn(
        'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
        className
      )}
      {...props}
    />
  )
);
TabsList.displayName = 'TabsList';

// Keep TabsTrigger definition, but use TabsTriggerPrimitive
type TabsTriggerProps = React.ComponentPropsWithoutRef<typeof TabsTriggerPrimitive>;

const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsTriggerPrimitive>, TabsTriggerProps>(
  ({ className, value, children, ...props }, ref) => {
    
    // Define base classes
    const baseClasses = "inline-flex items-center justify-center whitespace-nowrap rounded-full px-6 py-3 md:px-8 md:py-4 text-sm font-semibold transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background transform hover:scale-[1.02]";

    // Define conditional classes using Tailwind Arbitrary Variants for data-state
    let conditionalClasses = "";
    if (value === 'employer') {
      conditionalClasses = cn(
        "text-primary shadow-none", // Default/inactive state
        "[&[data-state=active]]:bg-primary [&[data-state=active]]:text-primary-foreground [&[data-state=active]]:shadow-neumorphic", // Active state styles
        "hover:bg-primary/10 [&[data-state=active]]:hover:bg-primary/90" // Hover styles for inactive/active
      );
    } else { // active, passive
      conditionalClasses = cn(
        "text-secondary shadow-none", // Default/inactive state
        "[&[data-state=active]]:bg-secondary [&[data-state=active]]:text-primary-foreground [&[data-state=active]]:shadow-neumorphic", // Active state styles
        "hover:bg-secondary/10 [&[data-state=active]]:hover:bg-secondary/90" // Hover styles for inactive/active
      );
    }

    return (
      <TabsTriggerPrimitive
        ref={ref}
        value={value}
        className={cn(
          baseClasses,
          conditionalClasses,
          className // Allow overrides
        )}
        {...props}
      >
        {children} 
      </TabsTriggerPrimitive>
    );
  }
);
TabsTrigger.displayName = 'TabsTrigger';

// Keep TabsContent definition, but use TabsContentPrimitive
type TabsContentProps = React.ComponentPropsWithoutRef<typeof TabsContentPrimitive>;

const TabsContent = React.forwardRef<React.ElementRef<typeof TabsContentPrimitive>, TabsContentProps>((
  // Remove context usage
  { className, value, ...props }, ref) => {
    // const context = React.useContext(TabsContext); // Removed
    // if (!context) { // Removed
    //   throw new Error('TabsContent must be used within a Tabs component'); // Removed
    // }

    // const { value: selectedValue } = context; // Removed
    // const isSelected = selectedValue === value; // Radix handles visibility internally

    // if (!isSelected) return null; // Radix handles visibility internally

    return (
      <TabsContentPrimitive // Use imported primitive
        ref={ref}
        value={value}
        className={cn('mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', className)}
        {...props}
      />
    );
  }
);
TabsContent.displayName = 'TabsContent';

// Assign the imported Root directly to the desired export name
const Tabs = TabsRoot;

// Export the alias and other components
export { 
  Tabs, 
  TabsList, 
  TabsTrigger, 
  TabsContent 
}; 